<template>
  <component
    :is="fieldComponent"
    class="coro-scrollable-combobox"
    :model-value="modelValue"
    :label="label"
    :aria-label="label"
    :items="items"
    :rules="rules"
    :error-messages="errorMessages"
    variant="outlined"
    :persistent-placeholder="false"
    multiple
    v-bind="fieldTypeProps"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #message="{ message }">
      <div class="d-flex align-center">
        <v-icon icon="icon-warning-xs" color="red-faint" size="18" class="mr-1" />
        <span>{{ message }}</span>
      </div>
    </template>
    <template #chip="{ props: chipProps, item }">
      <v-chip
        v-bind="chipProps"
        :closable="true"
        variant="flat"
        size="default"
        :color="getChipColor(item.value)"
        close-icon="$closeCircle"
        :text="item.title"
      />
    </template>
  </component>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { VAutocomplete, VCombobox } from "vuetify/components";
import { AllowBlockTermsFieldType } from "@/constants/phishing";

export interface AllowBlockTermsOption {
  title: string;
  value: string;
}

export type AllowBlockTermsRule = (values: string[]) => boolean | string;

export default defineComponent({
  name: "AllowBlockTermsField",
  props: {
    modelValue: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    label: {
      type: String,
      required: true,
    },
    fieldType: {
      type: String as PropType<AllowBlockTermsFieldType>,
      default: AllowBlockTermsFieldType.FREE_TEXT,
    },
    items: {
      type: Array as PropType<AllowBlockTermsOption[]>,
      default: () => [],
    },
    rules: {
      type: Array as PropType<AllowBlockTermsRule[]>,
      default: () => [],
    },
    errorMessages: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    invalidTerms: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    isValidTerm: {
      type: Function as PropType<(value: string) => boolean>,
      required: true,
    },
  },
  emits: ["update:modelValue"],
  setup(props) {
    const isOptionsField = computed(() => props.fieldType === AllowBlockTermsFieldType.OPTIONS);

    const fieldComponent = computed(() => (isOptionsField.value ? VAutocomplete : VCombobox));

    const fieldTypeProps = computed(() =>
      isOptionsField.value
        ? {
            itemTitle: "title",
            itemValue: "value",
            returnObject: false,
            chips: true,
            closableChips: true,
          }
        : { menuIcon: "", hideNoData: true }
    );

    const getChipColor = (value: string) =>
      props.invalidTerms.includes(value) || !props.isValidTerm(value) ? "error" : "indigo-faint";

    return {
      fieldComponent,
      fieldTypeProps,
      getChipColor,
    };
  },
});
</script>
